import { useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import LoadingButton from '@mui/lab/LoadingButton';
import ReactSelect from 'react-select';
import { useDebounce } from '../../hooks/useDebounce';
import { useGetUsers } from '../../hooks/queries/users/useGetUsers';
import { useGetMoodleConfigs } from '../../hooks/queries/moodle/useGetMoodleConfigs';
import { useCreateCourseExclusion } from '../../hooks/queries/moodle/useCreateCourseExclusion';
import { useBulkExcludeEmployees } from '../../hooks/queries/moodle/useBulkExcludeEmployees';
import { useBulkExcludeEmployeeFromCourses } from '../../hooks/queries/moodle/useBulkExcludeEmployeeFromCourses';

const SELECT_STYLES = {
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
};

const FieldLabel = ({ children }) => (
  <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
    {children}
  </Typography>
);

const ExcludeUsersModal = ({ open, onClose, configuredCourses }) => {
  const [activeTab, setActiveTab] = useState(0);

  // Shared across all tabs
  const [selectedConfig, setSelectedConfig] = useState(null);

  // Tab 0 — single employee, single course
  const [search0, setSearch0] = useState('');
  const [employee0, setEmployee0] = useState(null);
  const [course0, setCourse0] = useState(null);
  const [reason0, setReason0] = useState('');

  // Tab 1 — multiple employees, single course
  const [search1, setSearch1] = useState('');
  const [employees1, setEmployees1] = useState([]);
  const [course1, setCourse1] = useState(null);
  const [reason1, setReason1] = useState('');

  // Tab 2 — single employee, multiple courses
  const [search2, setSearch2] = useState('');
  const [employee2, setEmployee2] = useState(null);
  const [courses2, setCourses2] = useState([]);
  const [reason2, setReason2] = useState('');

  const debouncedSearch0 = useDebounce(search0);
  const debouncedSearch1 = useDebounce(search1);
  const debouncedSearch2 = useDebounce(search2);

  const { users: users0, loading: usersLoading0 } = useGetUsers(debouncedSearch0);
  const { users: users1, loading: usersLoading1 } = useGetUsers(debouncedSearch1);
  const { users: users2, loading: usersLoading2 } = useGetUsers(debouncedSearch2);
  const { configs, loading: configsLoading } = useGetMoodleConfigs();

  const resetTab0 = () => { setSearch0(''); setEmployee0(null); setCourse0(null); setReason0(''); };
  const resetTab1 = () => { setSearch1(''); setEmployees1([]); setCourse1(null); setReason1(''); };
  const resetTab2 = () => { setSearch2(''); setEmployee2(null); setCourses2([]); setReason2(''); };

  const { mutate: mutate0, isPending: isPending0 } = useCreateCourseExclusion(() => {
    resetTab0();
    setSelectedConfig(null);
    onClose();
  });

  const { mutate: mutate1, isPending: isPending1 } = useBulkExcludeEmployees(() => {
    resetTab1();
    setSelectedConfig(null);
    onClose();
  });

  const { mutate: mutate2, isPending: isPending2 } = useBulkExcludeEmployeeFromCourses(() => {
    resetTab2();
    setSelectedConfig(null);
    onClose();
  });

  const courseOptions = (Array.isArray(configuredCourses) ? configuredCourses : []).map((c) => ({
    label: c.courseNameSnapshot,
    value: c.id,
  }));

  const configOptions = configs.map((c) => ({
    label: `${c.serviceName} — ${c.baseUrl}`,
    value: c.id,
  }));

  const toUserOption = (u) => ({
    label: `${u.firstName} ${u.lastName ?? ''} — ${u.emailId}`,
    value: u.id,
  });

  const employeeOptions0 = users0.map(toUserOption);
  const employeeOptions1 = users1.map(toUserOption);
  const employeeOptions2 = users2.map(toUserOption);

  const handleClose = () => {
    resetTab0();
    resetTab1();
    resetTab2();
    setSelectedConfig(null);
    setActiveTab(0);
    onClose();
  };

  const handleSubmit = () => {
    if (activeTab === 0 && employee0 && course0 && selectedConfig) {
      mutate0({
        courseConfigId: course0.value,
        payload: {
          employeeId: employee0.value,
          reason: reason0,
          courseId: course0.value,
          moodleConfigId: selectedConfig.value,
        },
      });
    } else if (activeTab === 1 && employees1.length > 0 && course1 && selectedConfig) {
      mutate1({
        courseConfigId: course1.value,
        payload: {
          employeeIds: employees1.map((e) => e.value),
          reason: reason1,
          moodleConfigId: selectedConfig.value,
        },
      });
    } else if (activeTab === 2 && employee2 && courses2.length > 0 && selectedConfig) {
      mutate2({
        employeeId: employee2.value,
        courseConfigIds: courses2.map((c) => c.value),
        reason: reason2,
        moodleConfigId: selectedConfig.value,
      });
    }
  };

  const isPending = isPending0 || isPending1 || isPending2;

  const isValid =
    !!selectedConfig &&
    (activeTab === 0
      ? !!employee0 && !!course0
      : activeTab === 1
        ? employees1.length > 0 && !!course1
        : !!employee2 && courses2.length > 0);

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle>Exclude from Course</DialogTitle>

      <Tabs
        value={activeTab}
        onChange={(_, v) => setActiveTab(v)}
        sx={{ px: 3, borderBottom: 1, borderColor: 'divider' }}
      >
        <Tab label="Single" />
        <Tab label="Bulk — Employees" />
        <Tab label="Bulk — Courses" />
      </Tabs>

      <DialogContent
        sx={{
          pt: 2,
          overflow: 'visible',
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
        }}
      >
        {/* Shared config picker */}
        <Box>
          <FieldLabel>Moodle Configuration</FieldLabel>
          <ReactSelect
            options={configOptions}
            isLoading={configsLoading}
            placeholder="Select Moodle config..."
            value={selectedConfig}
            onChange={setSelectedConfig}
            menuPortalTarget={document.body}
            styles={SELECT_STYLES}
          />
        </Box>

        {/* Tab 0 — single employee, single course */}
        {activeTab === 0 && (
          <>
            <Box>
              <FieldLabel>Employee</FieldLabel>
              <ReactSelect
                options={employeeOptions0}
                isLoading={usersLoading0}
                placeholder="Search by name..."
                inputValue={search0}
                value={employee0}
                onInputChange={setSearch0}
                onChange={setEmployee0}
                filterOption={() => true}
                menuPortalTarget={document.body}
                styles={SELECT_STYLES}
              />
            </Box>
            <Box>
              <FieldLabel>Course</FieldLabel>
              <ReactSelect
                options={courseOptions}
                placeholder="Select course..."
                value={course0}
                onChange={setCourse0}
                menuPortalTarget={document.body}
                styles={SELECT_STYLES}
              />
            </Box>
            <TextField
              label="Reason"
              multiline
              minRows={3}
              fullWidth
              value={reason0}
              onChange={(e) => setReason0(e.target.value)}
            />
          </>
        )}

        {/* Tab 1 — multiple employees, single course */}
        {activeTab === 1 && (
          <>
            <Box>
              <FieldLabel>Course</FieldLabel>
              <ReactSelect
                options={courseOptions}
                placeholder="Select course..."
                value={course1}
                onChange={setCourse1}
                menuPortalTarget={document.body}
                styles={SELECT_STYLES}
              />
            </Box>
            <Box>
              <FieldLabel>Employees</FieldLabel>
              <ReactSelect
                isMulti
                options={employeeOptions1}
                isLoading={usersLoading1}
                placeholder="Search and select employees..."
                inputValue={search1}
                value={employees1}
                onInputChange={setSearch1}
                onChange={setEmployees1}
                filterOption={() => true}
                menuPortalTarget={document.body}
                styles={SELECT_STYLES}
              />
            </Box>
            <TextField
              label="Reason"
              multiline
              minRows={3}
              fullWidth
              value={reason1}
              onChange={(e) => setReason1(e.target.value)}
            />
          </>
        )}

        {/* Tab 2 — single employee, multiple courses */}
        {activeTab === 2 && (
          <>
            <Box>
              <FieldLabel>Employee</FieldLabel>
              <ReactSelect
                options={employeeOptions2}
                isLoading={usersLoading2}
                placeholder="Search by name..."
                inputValue={search2}
                value={employee2}
                onInputChange={setSearch2}
                onChange={setEmployee2}
                filterOption={() => true}
                menuPortalTarget={document.body}
                styles={SELECT_STYLES}
              />
            </Box>
            <Box>
              <FieldLabel>Courses</FieldLabel>
              <ReactSelect
                isMulti
                options={courseOptions}
                placeholder="Select courses..."
                value={courses2}
                onChange={setCourses2}
                menuPortalTarget={document.body}
                styles={SELECT_STYLES}
              />
            </Box>
            <TextField
              label="Reason"
              multiline
              minRows={3}
              fullWidth
              value={reason2}
              onChange={(e) => setReason2(e.target.value)}
            />
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} disabled={isPending}>
          Cancel
        </Button>
        <LoadingButton
          variant="contained"
          loading={isPending}
          disabled={!isValid}
          onClick={handleSubmit}
        >
          Exclude
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

export default ExcludeUsersModal;
